import { notification } from 'antd';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useMerchantsStore } from 'store/merchants';
import { usePaymentInvoicesTableStore } from 'store/paymentInvoicesTable';
import { usePaymentInvoicesTableFiltersStore } from 'store/paymentInvoicesTableFilters';
import Loader from 'ui/Loader';
import Table from 'ui/Table';
import { dateHelpers } from 'utils/helpers/dateHelpers';
import { errorHelper } from 'utils/helpers/errorHelper';
import { getSortParams } from 'utils/helpers/tableHelpers';
import type { TComponent } from 'utils/types/component';

import { useOnce } from 'components/utils/hooks/useOnce';
import { usePageVisibility } from 'components/utils/hooks/usePageVisibility';

import type { IPaymentInvoice } from 'models/Invoice/Payment';
import type { TTableSort } from 'models/Table';

import { Filters } from './components/Filters';
import { CustomPagination } from './components/Paginations';
import { COLUMNS } from './columns';
import styles from './PaymentInvoicesTable.module.scss';

export const PaymentInvoicesTable: TComponent = () => {
    const { t } = useTranslation();

    const data = usePaymentInvoicesTableStore('rows');
    const loading = usePaymentInvoicesTableStore('isLoading');
    const getTable = usePaymentInvoicesTableStore('getTable');
    const exportPaymentInvoices = usePaymentInvoicesTableStore(
        'exportPaymentInvoices',
    );

    const body = usePaymentInvoicesTableFiltersStore('body');
    const params = usePaymentInvoicesTableFiltersStore('params');
    const getCurrenciesOptions =
        usePaymentInvoicesTableFiltersStore('getCurrencyFilter');
    const getPaymentMethodNamesOptions = usePaymentInvoicesTableFiltersStore(
        'getPaymentMethodNamesFilter',
    );
    const getSitesOptions =
        usePaymentInvoicesTableFiltersStore('getSitesFilter');
    const getMerchants = useMerchantsStore('getMerchants');

    const handleExport = async (email: string) => {
        const startDate = body.dateFilter?.createdAtFrom;
        const endDate = body.dateFilter?.createdAtTo;
        const finishedAtFromDate = body.finishedDateFilter?.finishedAtFrom;
        const finishedAtToDate = body.finishedDateFilter?.finishedAtTo;

        if (body.dateFilter && (!startDate || !endDate)) {
            return notification.error({
                message: t('invoices.payment.export.filterOutOfDateError'),
            });
        }

        if (
            body.finishedDateFilter &&
            (!finishedAtFromDate || !finishedAtToDate)
        ) {
            return notification.error({
                message: t('invoices.payment.export.filterOutOfDateError'),
            });
        }

        if (
            !(startDate && endDate) &&
            !(finishedAtFromDate && finishedAtToDate)
        ) {
            return notification.error({
                message: t('invoices.payment.export.filterOutOfDateError'),
            });
        }

        if (
            startDate &&
            endDate &&
            !dateHelpers.isWithin14Days(startDate, endDate)
        ) {
            return notification.error({
                message: t(
                    'invoices.payment.export.filterWrongDatePeriodError',
                ),
            });
        }

        if (
            finishedAtFromDate &&
            finishedAtToDate &&
            !dateHelpers.isWithin14Days(finishedAtFromDate, finishedAtToDate)
        ) {
            return notification.error({
                message: t(
                    'invoices.payment.export.filterWrongDatePeriodError',
                ),
            });
        }

        try {
            await exportPaymentInvoices({
                filter: body,
                email,
            });

            notification.success({
                message: t('invoices.payment.export.exportSuccess'),
            });
        } catch (error) {
            errorHelper.showApiErrorNotification(error);
        }
    };

    const handleSortTable = (
        _: number,
        sortParams: TTableSort<IPaymentInvoice>,
    ) => {
        const { property, order } = getSortParams(sortParams);

        const newParams = { ...params, sort: `${property},${order}` };

        getTable(body, newParams);
    };

    useOnce(
        () => true,
        () => {
            getTable(body, params);
            getCurrenciesOptions();
            getPaymentMethodNamesOptions();
            getSitesOptions();
            getMerchants();
        },
        [],
    );

    usePageVisibility(() => {
        getTable(body, params);
        getCurrenciesOptions();
        getMerchants();
    });

    return (
        <Loader active={loading}>
            <div className={styles.container}>
                <div className={cn(styles.actionBar, 'actionBar')}>
                    <Filters onExport={handleExport} />
                </div>
                <Table
                    scroll={{ x: 'max-content' }}
                    columns={COLUMNS}
                    tableRows={data}
                    onChange={handleSortTable}
                    rowKey={(record) => record.identity}
                    isResizable
                />
                <div className={styles.tableBottom}>
                    <CustomPagination />
                </div>
            </div>
        </Loader>
    );
};
